import React, { useState } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, FileText, Eye } from 'lucide-react';
import { superAdminAPI } from '../../services/api';

const BulkUploadDraftModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);

  const resetForm = () => {
    setFile(null);
    setError('');
    setUploadResult(null);
    setValidationResult(null);
    setShowValidationDetails(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setError('');
      setValidationResult(null);
      setShowValidationDetails(false);
    }
  };

  const handleValidateFile = async () => {
    if (!file) {
      setError('Please select a file to validate');
      return;
    }

    setValidating(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await superAdminAPI.validateDraftQuestionsFile(formData);
      setValidationResult(response.data);

      if (response.data.isValid) {
        setShowValidationDetails(true);
      } else {
        setShowValidationDetails(true);
      }

    } catch (error) {
      console.error('Error validating file:', error);
      setError(error.response?.data?.message || 'Failed to validate file');
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadErrorReport = async () => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await superAdminAPI.downloadDraftQuestionsErrorReport(formData);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `validation_errors_${file.name.replace(/\.[^.]+$/, '')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading error report:', error);
      setError('Failed to download error report');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    // Check if file has been validated
    if (!validationResult || !validationResult.isValid) {
      setError('Please validate the file first and ensure it has no errors');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('validateOnly', 'false');

      const response = await superAdminAPI.bulkUploadDraftQuestions(formData);

      setUploadResult(response.data);

      if (response.data.successCount > 0) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Upload Draft Questions</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!uploadResult ? (
            <form onSubmit={handleUpload} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
                <h3 className="font-medium mb-2">Upload Instructions:</h3>
                <ul className="text-sm space-y-1">
                  <li>• Upload an XLS or XLSX file with draft questions</li>
                  <li>• Questions will be created in DRAFT status for review</li>
                  <li>• Validate your file first to check for errors</li>
                  <li>• Maximum file size: 10MB</li>
                </ul>
              </div>



              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File (.xlsx, .xls)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {file ? file.name : 'Click to select a file or drag and drop'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Validation Section */}
              {file && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">File Validation</h4>
                    <button
                      type="button"
                      onClick={handleValidateFile}
                      disabled={validating}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Eye className="h-4 w-4" />
                      <span>{validating ? 'Validating...' : 'Validate File'}</span>
                    </button>
                  </div>

                  {/* Validation Results */}
                  {validationResult && showValidationDetails && (
                    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-gray-900">Validation Results</h5>
                        <div className={`flex items-center space-x-2 ${validationResult.isValid ? 'text-green-600' : 'text-red-600'}`}>
                          {validationResult.isValid ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <AlertCircle className="h-5 w-5" />
                          )}
                          <span className="font-medium">
                            {validationResult.isValid ? 'Valid' : 'Has Errors'}
                          </span>
                        </div>
                      </div>

                      {/* Summary Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">{validationResult.totalRows}</div>
                          <div className="text-xs text-blue-700">Total Rows</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{validationResult.validRows}</div>
                          <div className="text-xs text-green-700">Valid Rows</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-lg font-bold text-yellow-600">{validationResult.warningCount || 0}</div>
                          <div className="text-xs text-yellow-700">Warnings</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <div className="text-lg font-bold text-red-600">{validationResult.errorCount}</div>
                          <div className="text-xs text-red-700">Errors</div>
                        </div>
                      </div>

                      {/* Warning Details */}
                      {validationResult.warnings && validationResult.warnings.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h6 className="font-medium text-yellow-800">Validation Warnings</h6>
                            <button
                              type="button"
                              onClick={handleDownloadErrorReport}
                              className="flex items-center space-x-2 text-sm text-yellow-600 hover:text-yellow-800"
                            >
                              <Download className="h-4 w-4" />
                              <span>Download Report</span>
                            </button>
                          </div>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                            <div className="text-sm text-yellow-700 space-y-1">
                              {validationResult.warnings.slice(0, 5).map((warning, index) => (
                                <div key={index} className="flex items-start space-x-2">
                                  <span className="font-medium">Row {warning.rowNumber}:</span>
                                  <span>[{warning.warningType}] {warning.message}</span>
                                </div>
                              ))}
                              {validationResult.warnings.length > 5 && (
                                <div className="text-xs text-yellow-600 italic">
                                  ... and {validationResult.warnings.length - 5} more warnings. Download full report for details.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Error Details */}
                      {validationResult.errors && validationResult.errors.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h6 className="font-medium text-red-800">Validation Errors</h6>
                            <button
                              type="button"
                              onClick={handleDownloadErrorReport}
                              className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800"
                            >
                              <Download className="h-4 w-4" />
                              <span>Download Error Report</span>
                            </button>
                          </div>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                            <div className="text-sm text-red-700 space-y-1">
                              {validationResult.errors.slice(0, 10).map((error, index) => (
                                <div key={index} className="flex items-start space-x-2">
                                  <span className="font-medium">Row {error.rowNumber}:</span>
                                  <span>[{error.errorType}] {error.message}</span>
                                </div>
                              ))}
                              {validationResult.errors.length > 10 && (
                                <div className="text-xs text-red-600 italic">
                                  ... and {validationResult.errors.length - 10} more errors. Download full report for details.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Success Message */}
                      {validationResult.isValid && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2 text-green-700">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">File is ready for upload!</span>
                          </div>
                          <p className="text-sm text-green-600 mt-1">
                            All {validationResult.validRows} rows passed validation and can be uploaded as draft questions.
                            {validationResult.warningCount > 0 && (
                              <span className="text-yellow-600">
                                {' '}Note: {validationResult.warningCount} warning(s) found but upload can proceed.
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={loading || validating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={loading || validating || !file || !validationResult || !validationResult.isValid}
                >
                  {loading ? 'Uploading...' : 'Upload Questions'}
                </button>
              </div>
            </form>
          ) : (
            /* Upload Results */
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Complete</h3>
              </div>

              {/* Results Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{uploadResult.successCount || 0}</div>
                  <div className="text-sm text-green-700">Successful</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{uploadResult.errorCount || 0}</div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{uploadResult.totalCount || 0}</div>
                  <div className="text-sm text-blue-700">Total</div>
                </div>
              </div>

              {/* Error Details */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">Errors:</h4>
                  <div className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {uploadResult.errors.map((error, index) => (
                      <div key={index}>Row {error.row}: {error.message}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => setUploadResult(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Upload Another File
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadDraftModal;
